import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell
} from 'recharts';
import {
  ChartBarIcon,
  AdjustmentsHorizontalIcon,
  FunnelIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import dashboardData from './dashboardData.json';

// Color palette for the bars
const colorPalette = [
  '#000000', // black
  '#4B0082', // indigo
  '#6A0DAD', // purple
  '#8B5CF6', // violet
  '#A78BFA', // light violet
  '#C084FC', // orchid
  '#E879F9', // pinkish
  '#F472B6', // pink
  '#FB7185', // rose
  '#FB923C', // orange
  '#F59E0B', // amber
  '#FACC15'  // yellow
];

/**
 * Computes a dynamic domain for non-percentage charts.
 * Adds a small margin so bars at exact boundaries are visible.
 */
const getDynamicDomain = (chartData, marginFactor = 0.1) => {
  const values = chartData.map(item => item.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);

  let margin = (maxVal - minVal) * marginFactor;
  if (margin === 0) {
    // If all values are the same, create a small margin
    margin = Math.abs(maxVal * marginFactor) || 1;
  }
  let lower = minVal - margin;
  let upper = maxVal + margin;
  // Don’t allow negative lower bound
  if (lower < 0) lower = 0;

  return [lower, upper];
};

/**
 * Returns [0, 100] for percentage-based charts.
 */
const getPercentDomain = () => [0, 100];

/**
 * Renders the bars with our custom color palette and slight rounding at the top.
 * @param {Array} chartData - The array of data (to map over for coloring)
 */
const renderColoredBars = (chartData) => (
  <Bar dataKey="value" barSize={30} radius={[4, 4, 0, 0]}>
    {chartData.map((entry, index) => {
      const color = colorPalette[index % colorPalette.length];
      return <Cell key={`cell-${index}`} fill={color} />;
    })}
  </Bar>
);

/**
 * Custom Tooltip for Recharts.
 * Centers its text and applies a smooth transition.
 * If the "percentage" prop is passed, the value is formatted with a '%' suffix.
 */
const CustomTooltip = ({ active, payload, label, percentage = false }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border border-gray-300 shadow rounded text-center transition duration-200 ease-out">
        {label && <p className="text-xs font-semibold text-gray-700 mb-1">{label}</p>}
        {payload.map((entry, index) => (
          <p key={`tooltip-${index}`} className="text-sm text-gray-900">
            {percentage ? `${entry.value}%` : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const CurriculumAnalyticsDashboard = () => {
  // Store dashboard data in state
  const [data] = useState(dashboardData);

  return (
    <div className="mx-auto pt-2 mb-3" style={{ width: '92vw' }}>
      {/* Top Row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
        <h1 className="text-2xl font-bold mb-2 md:mb-0">Academics</h1>
        <div className="flex items-center space-x-4">
          <button className="flex items-center text-blue-600 text-sm font-semibold">
            <ChartBarIcon className="w-4 h-4 mr-1" />
            Chart View
          </button>
          <button className="flex items-center text-blue-600 text-sm font-semibold">
            <AdjustmentsHorizontalIcon className="w-4 h-4 mr-1" />
            Customize View
          </button>
          <button className="flex items-center text-blue-600 text-sm font-semibold">
            <FunnelIcon className="w-4 h-4 mr-1" />
            Advanced Filters (1)
          </button>
          <div className="relative">
            <input
              type="text"
              placeholder="Type here to search"
              className="border border-gray-300 text-sm rounded-full pl-3 pr-9 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            <MagnifyingGlassIcon
              className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none"
            />
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-4 mb-3 justify-end">
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium">Period</label>
          <select className="border rounded px-2 py-1 text-sm w-40" defaultValue="">
            <option value="" disabled hidden>
              Term
            </option>
            <option>Term 1</option>
            <option>Term 2</option>
            <option>Term 3</option>
          </select>
        </div>
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium">Type</label>
          <select className="border rounded px-2 py-1 text-sm w-40" defaultValue="">
            <option value="" disabled hidden>
              Program
            </option>
            <option>Undergraduate</option>
            <option>Graduate</option>
          </select>
        </div>
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium">Timeframe</label>
          <select className="border rounded px-2 py-1 text-sm w-40" defaultValue="">
            <option value="" disabled hidden>
              Current Semester
            </option>
            <option>Fall 2023</option>
            <option>Spring 2024</option>
            <option>Full Year</option>
          </select>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Credit Hours Growth (percentage) */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-base font-semibold mb-2">Credit Hours Growth</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={data.creditHoursGrowth}
              barCategoryGap="20%"
              margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="program"
                tick={{ fontSize: 12, angle: -45, textAnchor: 'end' }}
                interval={0}
              />
              <YAxis
                domain={getPercentDomain()}
                tick={{ fontSize: 12 }}
                tickFormatter={(val) => `${val}%`}
              />
              <Tooltip content={<CustomTooltip percentage />} />
              <ReferenceLine
                y={10}
                stroke="#7c3aed"
                strokeWidth={2}
                strokeDasharray="4 4"
              />
              {renderColoredBars(data.creditHoursGrowth)}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Retention Rate (percentage) */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-base font-semibold mb-2">Retention Rate</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={data.retentionRate}
              barCategoryGap="20%"
              margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="program"
                tick={{ fontSize: 12, angle: -45, textAnchor: 'end' }}
                interval={0}
              />
              <YAxis
                domain={getPercentDomain()}
                tick={{ fontSize: 12 }}
                tickFormatter={(val) => `${val}%`}
              />
              <Tooltip content={<CustomTooltip percentage />} />
              <ReferenceLine
                y={80}
                stroke="#7c3aed"
                strokeWidth={2}
                strokeDasharray="4 4"
              />
              {renderColoredBars(data.retentionRate)}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Graduation Rate (percentage) */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-base font-semibold mb-2">Graduation Rate</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={data.graduationRate}
              barCategoryGap="20%"
              margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="program"
                tick={{ fontSize: 12, angle: -45, textAnchor: 'end' }}
                interval={0}
              />
              <YAxis
                domain={getPercentDomain()}
                tick={{ fontSize: 12 }}
                tickFormatter={(val) => `${val}%`}
              />
              <Tooltip content={<CustomTooltip percentage />} />
              <ReferenceLine
                y={60}
                stroke="#7c3aed"
                strokeWidth={2}
                strokeDasharray="4 4"
              />
              {renderColoredBars(data.graduationRate)}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Avg. Credit Hours/Student (not a percentage) */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-base font-semibold mb-2">
            Avg. Credit Hours/Student
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={data.avgCreditHoursPerStudent}
              barCategoryGap="20%"
              margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="program"
                tick={{ fontSize: 12, angle: -45, textAnchor: 'end' }}
                interval={0}
              />
              <YAxis
                domain={getDynamicDomain(data.avgCreditHoursPerStudent)}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine
                y={15}
                stroke="#7c3aed"
                strokeWidth={2}
                strokeDasharray="4 4"
              />
              {renderColoredBars(data.avgCreditHoursPerStudent)}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Course Conflicts (not a percentage) */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-base font-semibold mb-2">Course Conflicts</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={data.courseConflicts}
              barCategoryGap="20%"
              margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="program"
                tick={{ fontSize: 12, angle: -45, textAnchor: 'end' }}
                interval={0}
              />
              <YAxis
                domain={getDynamicDomain(data.courseConflicts)}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine
                y={3}
                stroke="#7c3aed"
                strokeWidth={2}
                strokeDasharray="4 4"
              />
              {renderColoredBars(data.courseConflicts)}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Balanced Sections (percentage) */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-base font-semibold mb-2">Balanced Sections</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={data.balancedSections}
              barCategoryGap="20%"
              margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="program"
                tick={{ fontSize: 12, angle: -45, textAnchor: 'end' }}
                interval={0}
              />
              <YAxis
                domain={getPercentDomain()}
                tick={{ fontSize: 12 }}
                tickFormatter={(val) => `${val}%`}
              />
              <Tooltip content={<CustomTooltip percentage />} />
              <ReferenceLine
                y={60}
                stroke="#7c3aed"
                strokeWidth={2}
                strokeDasharray="4 4"
              />
              {renderColoredBars(data.balancedSections)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default CurriculumAnalyticsDashboard;
